import Vue from "vue";
import VueRouter from "vue-router";
import Home from "../views/Home.vue";
import Store from "../store/index.js";
import Meta from "vue-meta";

import { Token_EmailEncrypt, Token_Email, Token_Guid, Profile_Get, Token_SilentSignIn, Token_HandoffRedeem } from "@/apis/api";
import { ENABLE_NOTIFICATIONS } from "@/config/features";
import { isCustomDomain } from "@/utils/custom-domain";
import { takeHandoffCodeFromUrl, consumeHandoffCode, rememberReferralFromUrl } from "@/utils/custom-domain-auth";
import { openLoginDialog, LOGIN_FLOW_ROUTES } from "@/utils/loginDialog";

Vue.use(VueRouter);
Vue.use(Meta);

const routes = [
  {
    path: "/",
    name: "Home",
    component: Home,
    meta: {
      title: "首頁",
      requiresAuth: false,
    },
  },
  {
    path: "/notifications",
    name: "NotificationCenter",
    component: () => import("../views/Member/NotificationCenter.vue"),
    meta: {
      title: "通知中心",
      requiresAuth: true,
    },
    // 通知功能暫未上正式：直接以網址進入時導回首頁
    beforeEnter: (to, from, next) => {
      if (ENABLE_NOTIFICATIONS) next();
      else next({ name: "Home" });
    },
  },
  {
    path: "/about",
    name: "About",
    // route level code-splitting
    // this generates a separate chunk (about.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    component: () => import(/* webpackChunkName: "about" */ "../views/About.vue"),
  },
  {
    path: "/signin",
    name: "SignIn",
    component: () => import("../views/SignIn.vue"),
    meta: {
      title: "登入",
      requiresAuth: false,
    },
  },
  {
    // 舊的「帳號與安全」頁（2026-10-21 棄用）：修改密碼、登入中的裝置搬到會員中心的「帳號安全」分頁
    path: "/accountSetting",
    name: "AccountSetting",
    redirect: { name: "Center", query: { tab: "security" } },
  },
  {
    // 社群站、AI 站登入對話框裡內嵌的登入畫面（見 views/EmbedLogin.vue）
    path: "/embed/login",
    name: "EmbedLogin",
    component: () => import("../views/EmbedLogin.vue"),
    meta: {
      title: "登入",
      requiresAuth: false,
      plainLayout: true,
      embed: true,
    },
  },
  {
    // 子站自訂網域的登入交接：登入完帶交接碼回到子站網域（見 utils/custom-domain-auth.js）
    path: "/auth/handoff",
    name: "AuthHandoff",
    component: () => import("../views/AuthHandoff.vue"),
    meta: {
      title: "登入中",
      requiresAuth: false,
      plainLayout: true,
    },
  },
  {
    path: "/loginSuccess",
    name: "LoginSuccess",
    component: () => import("../views/LoginSuccess.vue"),
    meta: {
      title: "登入成功",
      requiresAuth: false,
    },
  },
  {
    // App 的第三方登入在瀏覽器分頁完成後回到 App 的網址（App Link）。見 utils/nativeApp.js
    path: "/app-return/:action",
    name: "AppReturn",
    component: () => import("../views/AppReturn.vue"),
    meta: {
      title: "回到小芋頭 App",
      requiresAuth: false,
    },
  },
  {
    path: "/serviceTemp",
    name: "ServiceTemp",
    component: () => import("../views/Services/ServiceTemp.vue"),
    children: [
      {
        path: "/service",
        name: "Service",
        component: () => import("../views/Services/Service.vue"),
        meta: {
          title: "服務首頁",
          requiresAuth: false,
        },
      },
      {
        path: "/adsApply",
        name: "AdsApply",
        component: () => import("../views/Services/AdsApply.vue"),
        meta: {
          title: "廣告申請",
          requiresAuth: true,
        },
      },
      {
        path: "/ads",
        name: "Ads",
        component: () => import("../views/Services/Ads.vue"),
        meta: {
          title: "廣告總覽",
          requiresAuth: false,
        },
      },
    ],
  },
  {
    path: "/profileTemp",
    name: "ProfileTemp",
    component: () => import("../views/Profiles/ProfileTemp.vue"),
    children: [
      {
        path: "/profile",
        name: "Profile",
        component: () => import("../views/Profiles/Profile.vue"),
        meta: {
          title: "個人資訊",
          requiresAuth: true,
        },
      },
      {
        path: "/share",
        name: "Share",
        component: () => import("../views/Profiles/Share.vue"),
        meta: {
          title: "分享",
          requiresAuth: false,
        },
      },
    ],
  },
  {
    path: "/center",
    name: "Center",
    component: () => import("../views/Member/Center.vue"),
    meta: {
      title: "小芋頭會員",
      requiresAuth: true,
    },
  },
  {
    // 刪除帳號（Google Play 規定 App 內要有入口，也要有不用 App 的網址：這頁不用登入也打得開，會請對方登入再申請）
    path: "/account-deletion",
    name: "AccountDeletion",
    component: () => import("../views/Member/AccountDeletion.vue"),
    meta: {
      title: "刪除帳號",
      requiresAuth: false,
    },
  },
  {
    // 手機的「個人中心」（views/Member/MemberHome.vue）2026-10 先隱藏：底部導覽列改回「更多」清單，
    // 舊網址轉到會員中心。要再開放時把 redirect 換回 component: () => import("../views/Member/MemberHome.vue")，
    // 並把 MemberHome 加回 utils/appTabBar.js
    path: "/me",
    name: "MemberHome",
    redirect: { name: "Center" },
  },
  {
    path: "/order",
    name: "Order",
    component: () => import("../views/Member/Order.vue"),
    meta: {
      title: "購買飼料點數",
      requiresAuth: true,
    },
  },
  {
    path: "/order/success",
    name: "OrderSuccess",
    component: () => import("../views/Member/OrderSuccess.vue"),
    meta: {
      title: "購買飼料點數成功",
      requiresAuth: true,
    },
  },
  {
    path: "/upgrade",
    name: "Upgrade",
    component: () => import("../views/Member/Upgrade.vue"),
    meta: {
      title: "金豐小芋頭平台創業",
      requiresAuth: false,
    },
  },
  {
    path: "/upgrade/form",
    name: "UpgradeForm",
    component: () => import("../views/Member/UpgradeForm.vue"),
    meta: {
      title: "金豐小芋頭平台創業表單",
      requiresAuth: true,
    },
  },
  {
    path: "/product",
    name: "Product",
    component: () => import("../views/Products/Product.vue"),
    meta: {
      title: "小芋頭商品中心",
      requiresAuth: false,
    },
  },
  {
    path: "/product/:id/:slug?",
    name: "ProductDetail",
    component: () => import("../views/Products/ProductDetail.vue"),
    props: true, // 讓 id/slug 變成元件 props
    meta: { title: "商品內頁", requiresAuth: false },
  },
  {
    path: "/product/:id/:slug?/form",
    name: "ProductForm",
    component: () => import("../views/Products/ProductForm.vue"),
    props: true, // 讓 id/slug 變成元件 props
    meta: { title: "商品表單", requiresAuth: true },
  },
  {
    path: "/order/result",
    name: "SalesOrderResult",
    component: () => import("../views/Products/SalesOrderResult.vue"),
    props: true,
    // requiresAuth: false — 付款完成後藍新會 Redirect 回此頁，
    // 這是全新的頁面載入，不能要求登入，否則 Router Guard 會把使用者踢走
    meta: { title: "訂單結果", requiresAuth: false },
  },
  {
    path: "/disclaimer",
    name: "Disclaimer",
    component: () => import("../views/Disclaimer.vue"),
    meta: {
      title: "免責聲明",
      requiresAuth: false,
    },
  },
  {
    path: "/privacyPolicy",
    name: "PrivacyPolicy",
    component: () => import("../views/PrivacyPolicy.vue"),
    meta: {
      title: "隱私權政策",
      requiresAuth: false,
    },
  },
  {
    // 會員服務條款（2026-10-28）：註冊畫面的「註冊即同意」與頁尾連過來
    path: "/terms",
    name: "Terms",
    component: () => import("../views/Terms.vue"),
    meta: {
      title: "會員服務條款",
      requiresAuth: false,
    },
  },
  {
    path: "/images",
    name: "Images",
    component: () => import("../views/Images.vue"),
    meta: {
      title: "圖片",
      requiresAuth: false,
    },
  },
  {
    path: "/qrcode",
    name: "QRcode",
    component: () => import("../views/QRcode.vue"),
    meta: {
      title: "QRcode",
      requiresAuth: false,
    },
  },
  {
    path: "/commission",
    name: "Commission",
    component: () => import("../views/Commission.vue"),
    meta: {
      title: "Commission",
      requiresAuth: false,
    },
  },
  {
    path: "/yentity",
    name: "Yentity",
    component: () => import("../views/Projects/yentity.vue"),
    meta: {
      title: "yentity",
      requiresAuth: false,
    },
  },
  {
    path: "/yentity/about",
    name: "YentityAbout",
    component: () => import("../views/Projects/yentityAbout.vue"),
    meta: {
      title: "Yentity - About",
      requiresAuth: false,
    },
  },
  {
    path: "/yentity/outfit",
    name: "YentityOutfit",
    component: () => import("../views/Projects/outfit.vue"),
    meta: {
      title: "Yentity - Outfit",
      requiresAuth: false,
    },
  },
  {
    path: "/yentity/fashion-show",
    name: "YentityFashionShow",
    component: () => import("../views/Projects/YentityFashionShow.vue"),
    meta: {
      title: "Yentity - FashionShow",
      requiresAuth: false,
    },
  },
  {
    path: "/jhaoho",
    name: "Jhaoho",
    component: () => import("../views/Projects/Jhaoho.vue"),
    meta: {
      title: "朝和生醫",
      requiresAuth: false,
    },
  },

  {
    path: "/error",
    name: "Error",
    component: () => import("../views/Error.vue"),
    meta: {
      title: "error",
      requiresAuth: false,
    },
  },
  // 自訂頁面（迷你 CMS）。用 `*` 吃掉 /p/ 之後的整段路徑，所以 slug 可以有子路徑，
  // 例如 /p/yentity/outfit ——元件裡讀 $route.params.pathMatch 拿到 "yentity/outfit"。
  //
  // 為什麼要有 /p 前綴：站上已有 30 幾條硬編碼的頂層路由（/product、/service、/yentity…）。
  // 若讓自訂頁直接吃 /:專案/:子頁，設計師建一個叫 product 的專案就會跟現有路由對撞，
  // 而且是沉默的。加上前綴等於把 CMS 的命名空間隔開。
  //
  // 必須排在下面那條 `*` 之前，否則會先被導去 /error。
  {
    path: "/p/*",
    name: "CustomPage",
    component: () => import("../views/CustomPage.vue"),
    meta: {
      requiresAuth: false,
      // 不要套平台的背景圖，版面自己決定（見下方 beforeEach 的 plainLayout 判斷）
      plainLayout: true,
    },
  },
  {
    path: "*",
    redirect: "/error",
  },
];

/**
 * 子站自己的網域（例如 www.blackhorn.tw）上的路由。
 *
 * 整個網址空間都屬於那個子站，所以平台那 30 幾條路由一條都不掛 ——
 * 掛了的話 www.blackhorn.tw/product 會開出平台的商品頁，
 * 在一個對外的品牌網站上出現別人的商店頁面，比 404 糟得多。
 *
 * 只留兩條：什麼都接的自訂頁，以及錯誤頁。
 * 需要登入的路由一條都沒有，所以 beforeEach 那段導去 SignIn 的邏輯不會觸發。
 * header 的會員區照樣有，但登入、會員中心這些頁面都到平台網域開，
 * 登入完再用交接碼把登入狀態帶回來（見 utils/custom-domain-auth.js）。
 */
const customDomainRoutes = [
  {
    path: "/error",
    name: "Error",
    component: () => import("../views/Error.vue"),
    meta: {
      title: "error",
      requiresAuth: false,
      plainLayout: true,
    },
  },
  {
    path: "*",
    name: "CustomPage",
    component: () => import("../views/CustomPage.vue"),
    meta: {
      requiresAuth: false,
      plainLayout: true,
    },
  },
];

// 平台的路由表。子站網域的會員區要用它解析平台頁面的網址（custom-domain-auth 的 platformHref）
export const platformRoutes = routes;

// 平台登入完轉回子站網域時帶著 #taro-handoff=…：要在建立 router 之前收走，見函式說明
takeHandoffCodeFromUrl();
// 從分享連結進來時記住推薦碼（只跟著這個分頁），逛到別頁才按登入／註冊也掛得上推薦人。
// 以前只有子站網域記；平台改用登入對話框後（不再換到網址帶 refc 的登入頁），平台也要記
rememberReferralFromUrl();

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes: isCustomDomain() ? customDomainRoutes : routes,
  scrollBehavior(to, from, savedPosition) {
    // 只有 query 改變（同路徑 replace）時，不做任何捲動
    // 例如：ProductDetail 選規格時 updateQueryFromSelection 呼叫 $router.replace()
    if (to.path === from.path && to.name === from.name) {
      return false;
    }
    if (savedPosition) {
      return savedPosition;
    } else if (to.hash) {
      return { selector: to.hash, offset: { x: 0, y: 50 } };
    } else {
      return { x: 0, y: 0 };
    }
  },
});

/*
  部署新版後，舊分頁換頁沒反應（ChunkLoadError: Loading chunk xx failed）。

  每個頁面是各自的檔案，檔名帶內容雜湊；部署會把舊版的檔案刪掉。部署前就開著的分頁
  還記得舊檔名，換到還沒開過的頁面時抓不到，畫面就停住，要重新整理才好。
  遇到這種錯誤時直接重新載入到要去的那一頁，拿到新版就正常了。

  10 秒內只重載一次：真的抓不到（斷線、伺服器掛了）時不要無限重新整理。
*/
const CHUNK_RELOAD_KEY = "chunk-reload-at";

router.onError((err) => {
  const msg = String((err && err.message) || err || "");
  const isChunkError = (err && err.name === "ChunkLoadError") || /Loading (CSS )?chunk [\w-]+ failed/i.test(msg);
  if (!isChunkError) return;

  try {
    const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0);
    if (Date.now() - last < 10000) return;
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  } catch (e) {
    /* 無痕模式存不了：照樣重載一次 */
  }

  // 要去的那一頁（還沒切過去，所以不是目前網址）；取不到就重載目前這一頁
  const target = router.history && router.history.pending;
  window.location.assign(target ? router.resolve(target.fullPath).href : window.location.href);
});

const pickQueryValue = (value) => {
  const raw = Array.isArray(value) ? value[0] : value;
  if (typeof raw !== "string") return "";
  const text = raw.trim();
  return text && text !== "null" && text !== "undefined" ? text : "";
};

const hydrateSessionFromQuery = async (to) => {
  const token = pickQueryValue(to.query?.t);
  const emailEncrypt = pickQueryValue(to.query?.emailEncrypt);

  if (token) {
    localStorage.setItem("_token", token);
    await Store.dispatch("GetEmailEncrypt");
    return Store.state.signinStatus;
  }

  if (emailEncrypt) {
    Store.commit("SetSigninStatus", {
      status: true,
      username: emailEncrypt,
    });
    return true;
  }

  return false;
};

/**
 * 子站網域：平台登入完帶回來的交接碼，換成這個網域自己的登入狀態。
 * 換不到（逾時、已用過）就當作沒登入，繼續走一般流程。
 */
const hydrateSessionFromHandoff = async () => {
  const code = consumeHandoffCode();
  if (!code) return false;

  try {
    const res = await Token_HandoffRedeem(code);
    const token = res && res.data && res.data.token;
    if (!token) return false;

    localStorage.setItem("_token", token);
    await Store.dispatch("GetEmailEncrypt");
    return Store.state.signinStatus;
  } catch (e) {
    console.warn("[Session] 登入交接碼換不到登入狀態（可能已逾時或用過）：", e);
    return false;
  }
};

const syncSession = async (to) => {
  try {
    if (await hydrateSessionFromHandoff()) return;
    if (await hydrateSessionFromQuery(to)) return;

    const token = localStorage.getItem("_token");
    if (token && token !== "null" && token !== "undefined") {
      await Store.dispatch("GetEmailEncrypt");
      if (Store.state.signinStatus) return;
    }

    await Store.dispatch("SilentSignIn");
  } catch (e) {
    // 靜默登入 / 取回 session 失敗（例如 API 逾時或 HTTP/2 錯誤）→ 視為未登入。
    // 絕不可讓 beforeEach 因此丟例外而中止導航，否則會卡在載入畫面、按鈕全部失效。
    console.warn("[Session] syncSession 失敗，改以未登入狀態繼續：", e);
  }
};

router.beforeEach(async (to, from, next) => {
  Store.commit("GetUserDevice"); // 取得使用者裝置
  let userDevice = Store.state.userDevice;

  // 看有沒有串字串
  // 內嵌在社群、AI 站對話框裡的登入畫面不要轉（轉的是 iframe 自己，LINE 不會處理）
  if (userDevice.line && !to.meta.embed) {
    const redirectUrl = to.fullPath.includes("?") ? to.fullPath + "&openExternalBrowser=1" : to.fullPath + "?openExternalBrowser=1";
    window.location.href = redirectUrl;
    return false;
  }

  if (typeof window.localStorage === "undefined") {
    alert("您的瀏覽器不支援 Web Storage，請更換支援的瀏覽器。");
  }

  // 抓queryString (from=) 並在Vuex設定從哪個網站來
  Store.commit("SetFromSiteConfig", to.query.from);
  // 抓queryString (from_cmid=) 並在Vuex設定從哪個社群來
  Store.commit("SetFromSiteCmidConfig", to.query.from_cmid);

  // #region 看router 判斷要不要背景圖
  //
  // 新頁面請改用 `meta: { plainLayout: true }`，不要再往下面這個字串陣列加名字 ——
  // 陣列的問題是新增路由時很容易忘記同步，而漏掉的後果（頁面吃到平台背景圖）
  // 只有真的打開那頁才會發現。下面的陣列是既有頁面，維持現狀不動。
  let routers = ["Home", "Share", "Center", "Order", "OrderSuccess", "Upgrade", "UpgradeForm", "Product", "ProductDetail", "ProductForm", "SalesOrderResult", "Yentity", "YentityAbout", "YentityOutfit", "YentityFashionShow", "Jhaoho"];
  const plainLayout = to.matched.some((record) => record.meta.plainLayout);
  Store.state.isSerivceRouter = plainLayout || ["ServiceTemp", "ProfileTemp"].includes(to.matched[0]?.name) || routers.includes(to.name);
  //#endregion

  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);

  // Prefer the login callback token, then use the refresh cookie as a fallback.
  // 保險：session 同步不論成功失敗都要繼續導航，避免 next() 未被呼叫而卡住整個 App。
  try {
    await syncSession(to);
  } catch (e) {
    console.warn("[Router] session 同步發生例外，仍繼續導航：", e);
  }

  if (requiresAuth && !Store.state.signinStatus) {
    const { refc } = to.query;

    // 在網站裡點過去的（不是直接打開網址）：原地跳出登入對話框，登入完再前往（見 utils/loginDialog.js）。
    // 子站自訂網域照舊，登入要到平台網域做
    if (from.matched.length > 0 && !isCustomDomain() && !LOGIN_FLOW_ROUTES.includes(from.name)) {
      openLoginDialog({ after: to.fullPath, refc: Array.isArray(refc) ? refc[0] : refc });
      return next(false);
    }

    // 1. 儲存目前的目標路徑，登入成功後 redirectIfHasSavedUrl 會用到
    localStorage.setItem("redirectAfterLogin", to.fullPath);

    // 2. 準備轉跳登入頁，並帶上當前的推薦碼 (refc)
    return next({
      name: "SignIn",
      query: {
        status: "signin",
        from: "lt",
        ...(refc ? { refc } : {}),
      },
    });
  }

  next();
});

router.afterEach((to) => {
  const { emailEncrypt, t, ...rest } = to.query || {};
  if (emailEncrypt || t) {
    // 只清一次：下一個 afterEach 再進來就沒有 emailEncrypt/t 了
    router
      .replace({
        name: to.name,
        params: to.params,
        query: rest,
        hash: to.hash,
      })
      .catch(() => { });
  }
});

// 初始路由「真正就緒」後才收掉開機載入畫面(紫色轉圈)：
// onReady 會等到初始路由的非同步 chunk 載入完成、且所有 beforeEach(含靜默登入)解析完畢，
// 再等兩個影格確保畫面已繪製，避免 App 剛掛載但頁面尚未 render 就假性收掉(假載入完成)。
router.onReady(
  () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (typeof window.__hideBootLoader === "function") window.__hideBootLoader();
      });
    });
  },
  () => {
    // 初始導航發生錯誤也要收掉，避免卡在載入畫面(index.html 另有 15 秒保底)
    if (typeof window.__hideBootLoader === "function") window.__hideBootLoader();
  }
);

export default router;
